import * as XLSX from 'xlsx';
import {
  normalizeMatriculeKey,
  refineConjointType,
  extractAgentMatricule,
  buildAgentImportKey
} from './dnsLinking';

/**
 * Renvoie true si la valeur représente une case cochée (X, 1, TRUE, OUI, VRAI).
 */
export const isMarked = (value) => {
  const normalized = String(value || '').trim().toUpperCase();
  return ['X', '1', 'TRUE', 'OUI', 'VRAI'].includes(normalized);
};

/**
 * Convertit une cellule Excel (date série, ISO, ou jj/mm/aaaa) en chaîne
 * ISO YYYY-MM-DD. Retourne null si la valeur n'est pas une date interprétable.
 */
export const parseDate = (dateValue) => {
  if (dateValue === null || dateValue === undefined || dateValue === '') return null;

  if (typeof dateValue === 'number') {
    const date = XLSX.SSF.parse_date_code(dateValue);
    if (!date || !date.y || !date.m || !date.d) return null;
    return `${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`;
  }

  if (typeof dateValue === 'string') {
    const sanitized = dateValue.trim();
    if (!sanitized) return null;

    if (/^\d{4}-\d{2}-\d{2}$/.test(sanitized)) {
      return sanitized;
    }

    const parts = sanitized.split('/');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      if (year && month && day) {
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }
  }

  return null;
};

/**
 * Parse les données 2D d'un fichier DNS et renvoie la liste des adhérents
 * valides, les lignes invalides et le nombre d'ayants-droit non rattachés.
 *
 * Important : cette fonction simule la résolution backend (`memberController.importDNS`)
 * pour offrir une prévisualisation fidèle à ce qui sera réellement importé.
 *
 * @param {Array<Array<any>>} data - Données issues de XLSX.utils.sheet_to_json(sheet, { header: 1 }).
 * @returns {{
 *   members: Array<object>,
 *   invalidRows: Array<{ rowNumber: number, matricule: string, nom: string, prenom: string, reasons: string[] }>,
 *   unlinkedCount: number
 * }}
 * @throws {Error} si le format DNS n'est pas reconnu.
 */
export const parseDNSFile = (data) => {
  const members = [];
  const invalidRows = [];
  let startRow = -1;

  // Détection de l'en-tête.
  for (let i = 0; i < data.length; i++) {
    const row = data[i];
    if (row[0] === 'Nb' || (row[1] && String(row[1]).toUpperCase().includes('LIEU'))) {
      startRow = i + 1;
      break;
    }
  }

  if (startRow === -1) {
    throw new Error('Format DNS non reconnu. Vérifiez que le fichier contient les en-têtes corrects.');
  }

  // PASSE 1 — Lecture séquentielle, validation et propagation de l'agent courant.
  let currentAgent = null;
  let agentCounter = 0;
  let childCounters = { conjoint: 0, enfant: 0 };

  for (let i = startRow; i < data.length; i++) {
    const row = data[i];
    const rowNumber = i + 1;

    if (row[1] && String(row[1]).includes('EFFECTIF')) break;

    const matricule = row[2] ? String(row[2]).trim() : '';
    const nom = row[3] ? String(row[3]).trim() : '';
    const prenom = row[4] ? String(row[4]).trim() : '';
    const fonction = row[5] ? String(row[5]).trim() : '';
    const isAgent = isMarked(row[6]);
    const isConjoint = isMarked(row[7]);
    const isEnfant = isMarked(row[8]);
    const typeMarksCount = [isAgent, isConjoint, isEnfant].filter(Boolean).length;

    let type = '';
    if (isConjoint) type = 'CONJOINT';
    else if (isEnfant) type = 'ENFANT';
    else if (isAgent) type = 'AGENT';

    const dateNaissance = parseDate(row[9]);
    const rawSexe = row[10] ? String(row[10]).trim() : '';
    const dateEmbauche = parseDate(row[11]);
    const lieuAffectation = row[1] ? String(row[1]).trim() : '';

    const lineErrors = [];
    if (typeMarksCount === 0) lineErrors.push('type non detecte (aucune case cochee)');
    else if (typeMarksCount > 1) lineErrors.push('type ambigu (plusieurs cases cochees)');
    if (!nom) lineErrors.push('nom manquant');
    if (row[9] && !dateNaissance) lineErrors.push('date de naissance invalide');
    if (row[11] && !dateEmbauche) lineErrors.push('date embauche invalide');

    if (lineErrors.length > 0) {
      invalidRows.push({
        rowNumber,
        matricule: matricule || '-',
        nom: nom || '-',
        prenom: prenom || '-',
        reasons: lineErrors
      });
      continue;
    }

    if (type === 'AGENT') {
      agentCounter += 1;
      const agentImportKey = buildAgentImportKey({ matricule }, agentCounter);
      currentAgent = { matricule: matricule || null, nom, prenom, key: agentImportKey };
      childCounters = { conjoint: 0, enfant: 0 };

      members.push({
        matricule: matricule || null,
        nom,
        prenom: prenom || null,
        fonction,
        type: 'AGENT',
        date_naissance: dateNaissance,
        sexe: rawSexe ? rawSexe.toUpperCase() : '',
        date_embauche: dateEmbauche,
        lieu_affectation: lieuAffectation,
        rowNumber,
        agent_import_key: agentImportKey
      });
    } else {
      const resolvedType = refineConjointType(type, rawSexe);

      // Génération d'un matricule auto si manquant et qu'on a un agent courant
      // avec matricule (ex. 001C, 001E1, …) — convention identique au backend.
      let finalMatricule = matricule || null;
      if (!finalMatricule && currentAgent && currentAgent.matricule) {
        if (resolvedType === 'CONJOINT' || resolvedType === 'CONJOINTE') {
          childCounters.conjoint++;
          finalMatricule = `${currentAgent.matricule}C${
            childCounters.conjoint > 1 ? childCounters.conjoint : ''
          }`;
        } else if (resolvedType === 'ENFANT') {
          childCounters.enfant++;
          finalMatricule = `${currentAgent.matricule}E${childCounters.enfant}`;
        }
      }

      members.push({
        matricule: finalMatricule,
        matricule_generated: !matricule && !!finalMatricule,
        nom,
        prenom: prenom || null,
        fonction,
        type: resolvedType,
        date_naissance: dateNaissance,
        sexe: rawSexe ? rawSexe.toUpperCase() : '',
        date_embauche: dateEmbauche,
        lieu_affectation: lieuAffectation,
        rowNumber,
        agent_matricule: currentAgent ? currentAgent.matricule : null,
        agent_import_key: currentAgent ? currentAgent.key : null,
        agent_nom: currentAgent ? currentAgent.nom : null,
        agent_prenom: currentAgent ? currentAgent.prenom : null
      });
    }
  }

  // PASSE 2 — Simulation de la résolution backend pour les orphelins :
  // on cherche un agent par `extractAgentMatricule(matricule)` parmi les
  // agents déjà détectés dans le fichier. Cela donne une preview fidèle.
  const agentsByKey = {};
  members.forEach((m) => {
    if (m.type === 'AGENT' && m.agent_import_key) {
      agentsByKey[m.agent_import_key] = m;
    }
  });

  members.forEach((dep) => {
    if (dep.type === 'AGENT' || dep.agent_import_key) return;

    const candidate = extractAgentMatricule(dep.matricule || '', dep.type);
    const candidateKey = normalizeMatriculeKey(candidate);
    if (candidateKey && agentsByKey[candidateKey]) {
      const agent = agentsByKey[candidateKey];
      dep.agent_import_key = agent.agent_import_key;
      dep.agent_matricule = agent.matricule || null;
      dep.agent_nom = agent.nom;
      dep.agent_prenom = agent.prenom;
    }
  });

  const unlinkedCount = members.filter(
    (m) => m.type !== 'AGENT' && !m.agent_import_key
  ).length;

  return { members, invalidRows, unlinkedCount };
};

/**
 * Calcule les statistiques d'un parsing DNS pour affichage.
 */
export const computeDNSStats = ({ members, invalidRows, unlinkedCount }) => ({
  agents: members.filter((m) => m.type === 'AGENT').length,
  conjoints: members.filter((m) => m.type === 'CONJOINT' || m.type === 'CONJOINTE').length,
  enfants: members.filter((m) => m.type === 'ENFANT').length,
  total: members.length,
  invalidRows: invalidRows.length,
  unlinkedDependents: unlinkedCount || 0
});
